import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { userManagementService, CustomerV2, CustomerWithContact } from '../../services/userManagementService';

interface Step2ContactSelectionProps {
  customersWithContact: CustomerWithContact[];
  onCustomersChange: (customers: CustomerWithContact[]) => void;
  selectedCustomers: CustomerV2[];
}

const Step2ContactSelection: React.FC<Step2ContactSelectionProps> = ({
  customersWithContact,
  onCustomersChange,
  selectedCustomers,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState<Set<string>>(new Set());

  // Fetch full customer data with expand for each selected customer
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (selectedCustomers.length === 0) {
        return;
      }

      setLoading(true);
      const updatedCustomers: CustomerWithContact[] = [];

      for (const customer of selectedCustomers) {
        // Check if we already have this customer with contact fields
        const existing = customersWithContact.find((c) => c.customerId === customer.id);
        if (existing && existing.availableFields.length > 0) {
          updatedCustomers.push(existing);
          continue;
        }

        try {
          setLoadingCustomers((prev) => new Set(prev).add(customer.id));
          console.log(`🔍 Fetching full customer data for: ${customer.id} - ${customer.red_rock_full_entity_name}`);
          console.log(`🔍 Customer object:`, customer);
          
          // Fetch the full customer record with expand
          // Pass red_rock_id if available (from view data)
          const fullCustomer = await userManagementService.fetchCustomerById(
            customer.id,
            customer.red_rock_id
          );
          console.log(`✅ Fetched customer data:`, fullCustomer);
          
          // Get contact fields from the full customer data
          const availableFields = userManagementService.getCustomerContactFields(fullCustomer);
          console.log(`📧 Contact fields found:`, availableFields);
          
          updatedCustomers.push({
            customerId: customer.id,
            customerName: fullCustomer.red_rock_full_entity_name || fullCustomer.company_name || 'Unknown',
            availableFields,
          });
        } catch (error: any) {
          console.error(`❌ Error fetching customer ${customer.id}:`, error);
          // Still add the customer but with no fields
          updatedCustomers.push({
            customerId: customer.id,
            customerName: customer.red_rock_full_entity_name || customer.company_name || 'Unknown',
            availableFields: [],
          });
        } finally {
          setLoadingCustomers((prev) => {
            const next = new Set(prev);
            next.delete(customer.id);
            return next;
          });
        }
      }

      setLoading(false);
      onCustomersChange(updatedCustomers);
    };

    fetchCustomerData();
  }, [selectedCustomers.map(c => c.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (customerId: string, fieldName: string, fieldValue: string) => {
    const updated = customersWithContact.map((customer) => {
      if (customer.customerId === customerId) {
        return {
          ...customer,
          selectedField: fieldName,
          selectedValue: fieldValue,
        };
      }
      return customer;
    });
    onCustomersChange(updated);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 2 of 3: Select Contact Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        For each selected customer, choose which email or phone field to use for the client user account.
      </Typography>

      {loading && customersWithContact.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading customer contact information...</Typography>
        </Box>
      ) : customersWithContact.length === 0 ? (
        <Alert severity="info">
          No customers selected. Please go back and select customers.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {customersWithContact.map((customer, index) => {
            const isLoading = loadingCustomers.has(customer.customerId);
            return (
              <Paper key={customer.customerId} sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  {index + 1}. {customer.customerName}
                </Typography>

                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 2 }} />
                    <Typography variant="body2">Loading contact information...</Typography>
                  </Box>
                ) : customer.availableFields.length === 0 ? (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    No contact information available for this customer. Please select a different customer.
                  </Alert>
                ) : (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id={`contact-field-label-${customer.customerId}`}>
                    Select Email or Phone
                  </InputLabel>
                  <Select
                    labelId={`contact-field-label-${customer.customerId}`}
                    id={`contact-field-select-${customer.customerId}`}
                    value={customer.selectedField || ''}
                    label="Select Email or Phone"
                    onChange={(e) => {
                      const selectedField = customer.availableFields.find(
                        (f) => f.fieldName === e.target.value
                      );
                      if (selectedField) {
                        handleFieldChange(
                          customer.customerId,
                          selectedField.fieldName,
                          selectedField.fieldValue
                        );
                      }
                    }}
                  >
                    {customer.availableFields.map((field) => (
                      <MenuItem key={field.fieldName} value={field.fieldName}>
                        {field.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {customer.selectedValue && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Selected: <strong>{customer.selectedValue}</strong>
                </Alert>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default Step2ContactSelection;

